<template>
  <div class="date-time-wrapper">
    <div class="picker-container date-picker-container" v-if="isShowDate()">
      <datetime
        v-model="pickedDate"
        :type="type"
        :input-class="setDisabled()"
        :placeholder="placeholder"
        :min-datetime="minDate"
        :max-datetime="maxDate"
        format="dd/MM/yyyy"
        value-zone="UTC+07:00"
        zone="UTC+07:00" />
    </div>
    <div class="picker-container time-picker-container" v-if="isShowTime()">
      <select
        v-model="hour"
        class="form-control form-control-time"
        :class="{disabled: disabled}">
        <option v-for="i in 24" :key="i" :value="i-1">{{transformTime(i-1)}}</option>
      </select>
      <span class="spliter">:</span>
      <select
        v-model="minute"
        class="form-control form-control-time"
        :class="{disabled: disabled}">
        <option v-for="i in 60" :key="i" :value="i-1">{{transformTime(i-1)}}</option>
      </select>
    </div>
  </div>
</template>

<script>
/* eslint no-shadow: "off" */
import dateFormatter from '@/utils/dateFormatter';

const Datetime = () => import(/* webpackChunkName: "Datetime" */ 'vue-datetime').then(({ Datetime }) => Datetime);

export default {
  mixins: [dateFormatter],
  props: {
    type: {
      type: String,
      default: 'dateTime',
    }, // date, dateTime, time
    placeholder: {
      type: String,
    },
    className: {
      type: String,
      default: 'form-control',
    },
    value: {
      type: [String, Date],
    },
    minimumDate: {
      type: [String, Date],
      default: null,
    },
    maximumDate: {
      type: [String, Date],
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Datetime,
  },
  data() {
    return {
      pickedDate: null,
      hour: '0',
      minute: '0',
      minDate: '',
      maxDate: '',
    };
  },
  created() {
    if (this.value) {
      this.mapDateTime(this.value);
    }
  },
  watch: {
    value(newValue) {
      this.mapDateTime(newValue);
    },
    pickedDate() {
      if (this.pickedDate) {
        const pickedDate = this.setDateTime(this.pickedDate, this.hour, this.minute);
        const newDate = this.getValidatedDateTime(pickedDate);
        this.$emit('input', newDate);
        this.$emit('dateChanged', newDate);
      }
    },
    hour() {
      const pickedDate = this.setDateTime(this.pickedDate, this.hour, this.minute);
      const newDate = this.getValidatedDateTime(pickedDate);
      this.$emit('input', newDate);
    },
    minute() {
      const pickedDate = this.setDateTime(this.pickedDate, this.hour, this.minute);
      const newDate = this.getValidatedDateTime(pickedDate);
      this.$emit('input', newDate);
    },
    minimumDate(newDate) {
      if (newDate) {
        this.minDate = this.formatISO(newDate);
      } else {
        this.minDate = '';
      }
    },
    maximumDate(newDate) {
      if (newDate) {
        this.maxDate = this.formatISO(newDate);
      } else {
        this.maxDate = '';
      }
    },
  },
  methods: {
    onResetValue() {
      this.pickedDate = null;
      this.hour = 0;
      this.minute = 0;
    },
    transformTime(time) {
      if (time < 10) return `0${time}`;
      return time;
    },
    setDateTime(pickedDate, hour, minute) {
      if (pickedDate) {
        const date = new Date(pickedDate);
        date.setHours(hour);
        date.setMinutes(minute);
        return date;
      }
      return null;
    },
    mapDateTime(value) {
      if (value) {
        this.hour = this.getHours(value);
        this.minute = this.getMinutes(value);
        this.pickedDate = this.formatISO(value);
      } else {
        this.onResetValue();
      }
    },
    isShowDate() {
      if (this.type === 'dateTime' || this.type === 'date') {
        return true;
      }
      return false;
    },
    isShowTime() {
      if (this.type === 'dateTime' || this.type === 'time') {
        return true;
      }
      return false;
    },
    setDisabled() {
      if (this.disabled) {
        return `${this.className} disabled`;
      }
      return this.className;
    },
    getValidatedDateTime(newDate) {
      if (this.minimumDate && newDate < this.minimumDate) {
        return new Date(this.minimumDate.getTime() + 60000);
      }
      if (this.maximumDate && newDate > this.maximumDate) {
        return new Date(this.maximumDate.getTime() - 60000);
      }
      return newDate;
    },
  },
};
</script>

<style lang="scss">
@import '~vue-datetime/dist/vue-datetime.css';
@import '@/assets/scss/variables.scss';

select.form-control-time {
  min-height: unset;
  padding: $spacer * 0.375 $spacer * 0.5;
}

.disabled {
  background-color: #e9ecef !important;
  opacity: 1;
  pointer-events: none;
}

.date-time-wrapper {
  display: flex;

  .picker-container {
    &:nth-child(2) {
      margin-left: $spacer;
    }
  }

  .time-picker-container {
    display: flex;
    align-items: center;
    .spliter {
      margin: 0 $spacer / 3;
    }
  }
}

.vdatetime {
  .form-control {
    cursor: pointer;
    display: block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.5;
    color: #495057;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #dee2e6;
    border-radius: 0.25rem;
  }
}

.section-attachment-container {
  .vdatetime {
    .form-control {
      width: 126px;
    }
  }
}

.vdatetime-popup {
  font-size: 12px;
  .vdatetime-popup__header {
    background-color: $primary;
  }
  .vdatetime-popup__actions__button {
    color: $primary;
  }
  .vdatetime-calendar__month__day--selected:hover {
    background-color: transparent;
  }
  .vdatetime-calendar__month__day--selected, .vdatetime-calendar__month__day:hover:not(.vdatetime-calendar__month__day--disabled) {
    span {
      span {
        color: white;
        background: $primary;
      }
    }
  }
  .vdatetime-time-picker__item--selected {
    color: $primary;
  }
  .vdatetime-year-picker__item--selected {
    color: $primary;
  }
}

</style>
